import { useMemo, useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, Phone } from "lucide-react";
import { formatPhoneFr, validatePhoneFr } from "@/lib/formatters";

/**
 * Composant de saisie de numéro de téléphone français avec :
 *  - Auto-formatage "06 12 34 56 78" pendant la frappe
 *  - maxLength=14 (10 chiffres + 4 espaces) — bloque physiquement l'ajout de chiffres au-delà
 *  - Validation temps réel : 10 chiffres commençant par 0[1-9]
 *  - Feedback visuel (bordure verte/rouge + icône) sur blur ou dès que 10 chiffres saisis
 *  - Message d'aide sous le champ
 *  - Compatible avec les icônes gauches (prop `withIcon`)
 *
 * Props:
 *  - value: string (formaté ou brut)
 *  - onChange: (formatted: string) => void — reçoit toujours la version formatée
 *  - placeholder: string (defaut "06 12 34 56 78")
 *  - required: boolean — si true, un champ vide est invalide
 *  - showValidation: boolean — masque le feedback visuel si false (defaut true)
 *  - withIcon: boolean — affiche une icône téléphone à gauche (defaut false)
 *  - className: string additionnel
 *  - inputClassName: string additionnel pour l'input
 *  - "data-testid": string
 *  - ...rest → transmis à l'Input
 */
export default function PhoneInputFr({
  value = "",
  onChange,
  placeholder = "06 12 34 56 78",
  required = false,
  showValidation = true,
  withIcon = false,
  className = "",
  inputClassName = "",
  "data-testid": dataTestId,
  ...rest
}) {
  const [touched, setTouched] = useState(false);

  const formatted = useMemo(() => formatPhoneFr(value), [value]);
  // Keep the input in sync when the parent replaces value externally.
  useEffect(() => { /* no-op — formatted is derived */ }, [value]);

  const { valid, digits, error } = validatePhoneFr(value);
  const isEmpty = digits.length === 0;
  const showFeedback = showValidation && (touched || digits.length >= 10);
  const requiredError = required && isEmpty ? "Téléphone requis" : "";
  const displayError = requiredError || (showFeedback && !valid ? error : "");
  const showSuccess = showFeedback && valid && !isEmpty;

  const borderCls = displayError
    ? "border-red-500 focus-visible:ring-red-500"
    : showSuccess
      ? "border-green-500 focus-visible:ring-green-500"
      : "border-gray-300";

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="relative">
        {withIcon && (
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        )}
        <Input
          value={formatted}
          onChange={(e) => onChange?.(formatPhoneFr(e.target.value))}
          onBlur={() => setTouched(true)}
          placeholder={placeholder}
          type="tel"
          autoComplete="tel"
          inputMode="numeric"
          maxLength={20}
          data-testid={dataTestId}
          className={`rounded-none ${withIcon ? "pl-10" : ""} pr-8 ${borderCls} ${inputClassName}`}
          {...rest}
        />
        {showSuccess && (
          <CheckCircle2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-green-600 pointer-events-none" data-testid={dataTestId ? `${dataTestId}-valid` : undefined} />
        )}
        {displayError && (
          <XCircle className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-red-600 pointer-events-none" data-testid={dataTestId ? `${dataTestId}-invalid` : undefined} />
        )}
      </div>
      {displayError && (
        <p className="text-[11px] text-red-600" data-testid={dataTestId ? `${dataTestId}-error` : undefined}>{displayError}</p>
      )}
    </div>
  );
}
